/* @jsxImportSource react */
'use client';

import { forwardRef, useCallback, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Input, type TextInputProps } from '@opendoor/atoms/input';
import { cn } from '@opendoor/atoms/utils';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

export interface PasswordInputProps extends Omit<TextInputProps, 'type'> {
  /**
   * Accessible label for the visibility toggle. It stays constant across
   * states (the button always toggles visibility); the current state is exposed
   * separately via `aria-pressed`.
   * @default "Show password"
   */
  toggleLabel?: string;
}

/**
 * Password field with an in-field visibility toggle. Atoms' `Input` has no
 * adornment slot, so this composes the toggle as an absolutely-positioned
 * button over a right-padded `Input` (mirroring the `RedirectAuthButton`
 * atoms-composition precedent).
 *
 * The toggle is a real, focusable toggle button: `aria-pressed` announces
 * whether the password is revealed, and a `mousedown` `preventDefault` keeps
 * the caret in the input when it is clicked, so focus never leaves the field.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, analyticsName, toggleLabel = 'Show password', ...inputProps }, ref) => {
    const [visible, setVisible] = useState(false);

    const toggle = useCallback(() => setVisible((v) => !v), []);
    // Keep focus (and the caret) in the input when the toggle is clicked: a
    // pointer press on a button would otherwise move focus off the field.
    const preserveFocus = useCallback((event: React.MouseEvent) => event.preventDefault(), []);

    return (
      <Box className="relative w-full">
        <Input
          {...inputProps}
          ref={ref}
          type={visible ? 'text' : 'password'}
          analyticsName={analyticsName}
          className={cn('w-full pr-14', className)}
        />
        <Box
          onClick={toggle}
          onMouseDown={preserveFocus}
          // When the field is disabled the toggle goes with it — a disabled
          // field's value must not stay revealable, and the button must not
          // take focus or fire analytics.
          disabled={inputProps.disabled}
          aria-label={toggleLabel}
          aria-pressed={visible}
          analyticsName={analyticsName ? `${analyticsName}-visibility-toggle` : undefined}
          data-testid="password-visibility-toggle"
          className="absolute inset-y-0 right-0 items-center justify-center px-4 text-od-content-secondary disabled:cursor-default disabled:text-od-content-inactive-primary"
        >
          {visible ? <IconEyeOff size={20} aria-hidden /> : <IconEye size={20} aria-hidden />}
        </Box>
      </Box>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
